import React from 'react';

/**
 * Membership type eligibility rules. A type's `requirementOptions` are alternatives: the applicant
 * qualifies by providing ALL the documents of ANY ONE option (Degree + 3 yrs experience OR 5+ yrs
 * experience). A type saved before options existed has only `requiredDocuments`, which means the
 * single option "all of these".
 */

export const docKey = (n) => String(n || '').trim().replace(/\s+/g, ' ').toLowerCase();

/** The type's options, falling back to one option holding every required document. */
export function requirementOptionsOf(type) {
  const opts = (type?.requirementOptions || []).filter(o => (o?.documents || []).length);
  if (opts.length) return opts;
  const docs = type?.requiredDocuments || [];
  return docs.length ? [{ label: '', documents: docs }] : [];
}

/**
 * Which options the uploaded/ticked document names satisfy.
 * @returns {{ met: boolean, metIndex: number, closest: { index: number, missing: string[] } | null, options }}
 */
export function checkRequirements(type, providedNames) {
  const options = requirementOptionsOf(type);
  const have = new Set([...(providedNames || [])].map(docKey));
  const status = options.map((o, index) => {
    const missing = o.documents.filter(d => !have.has(docKey(d)));
    return { index, missing, provided: o.documents.length - missing.length };
  });
  const metIndex = status.findIndex(s => s.missing.length === 0);
  // Point at the option nearest completion; on a tie, the one the applicant has started on
  const closest = status.length
    ? [...status].sort((a, b) => a.missing.length - b.missing.length || b.provided - a.provided)[0]
    : null;
  return { met: options.length === 0 || metIndex !== -1, metIndex, closest, options };
}

/** Error text for a submit button, or '' when the rule is met. */
export function requirementsMessage(type, providedNames) {
  const { met, closest, options } = checkRequirements(type, providedNames);
  if (met) return '';
  if (options.length === 1) return `Please upload all required documents. Missing: ${closest.missing.join(', ')}`;
  return `Upload every document of at least one option. Closest is Option ${closest.index + 1}, still missing: ${closest.missing.join(', ')}`;
}

const Tick = ({ on }) => (
  <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${on ? 'bg-emerald-500 text-white' : 'border border-gray-300 bg-white'}`}>
    {on && <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" /></svg>}
  </span>
);

/**
 * "Provide one of these" panel for apply / upgrade / approval screens. Ticks show which documents
 * are already provided; the satisfied option is highlighted. Renders nothing when there is only
 * one option (the screen's own "required documents" list already says it).
 */
export const RequirementOptionsPanel = ({ type, providedNames = [], title = 'Eligibility: provide ONE of these options', className = '' }) => {
  const { options, metIndex } = checkRequirements(type, providedNames);
  if (options.length < 2) return null;
  const have = new Set([...providedNames].map(docKey));
  return (
    <div className={`rounded-xl border border-violet-100 bg-violet-50/40 p-4 ${className}`}>
      <p className="mb-3 text-sm font-medium text-gray-800">{title}</p>
      <div className="space-y-2">
        {options.map((o, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <div className="flex items-center gap-2 py-0.5">
                <span className="h-px flex-1 bg-violet-100" />
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-violet-500">or</span>
                <span className="h-px flex-1 bg-violet-100" />
              </div>
            )}
            <div className={`rounded-lg border bg-white px-3 py-2.5 ${metIndex === i ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-gray-700">Option {i + 1}{o.label ? `: ${o.label}` : ''}</p>
                {metIndex === i && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[0.6875rem] font-medium text-emerald-700">Complete</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                {o.documents.map((d, j) => (
                  <React.Fragment key={d}>
                    {j > 0 && <span className="text-[0.6875rem] font-semibold text-gray-400">+</span>}
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-700"><Tick on={have.has(docKey(d))} />{d}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
